import {
  IOvertimeRequestPaginatedListData,
  IOvertimeRequestService
} from './overtime-request.types';
import { AxiosInstance } from 'axios';
import { objectToFormData } from '@/utils/objectToFormData';
import {
  IAttachmentData,
  IMentionAttachmentCommentData,
  IOvertimeRequestData,
  IUserData
} from '@/models';
import {
  IFetchOvertimeRequestsFilterParams,
  IUpdateOvertimeStatePayload
} from '@/store/overtime-request';
import { Transition } from '@/models/transition';

export class OvertimeRequestService implements IOvertimeRequestService {
  private endpoint: string;

  constructor(private axios: AxiosInstance) {
    this.endpoint = 'v1/overtime/';
  }

  fetchOvertimeRequests(
    param?: IFetchOvertimeRequestsFilterParams
  ): Promise<IOvertimeRequestPaginatedListData> {
    return this.axios
      .get<IOvertimeRequestPaginatedListData>(`${this.endpoint}`, {
        params: {
          search: param?.search,
          page: param?.page,
          state: param?.state
        }
      })
      .then(({ data }) => data);
  }

  fetchOvertimeRequest(id: number): Promise<IOvertimeRequestData> {
    return this.axios
      .get<IOvertimeRequestData>(`${this.endpoint}${id}/`)
      .then(({ data }) => data);
  }

  createOvertimeRequest(
    data: IOvertimeRequestData
  ): Promise<IOvertimeRequestData> {
    const formData = objectToFormData(data);
    return this.axios
      .post<IOvertimeRequestData>(`${this.endpoint}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      .then(({ data }) => data);
  }

  updateOvertimeRequest(
    data: IOvertimeRequestData
  ): Promise<IOvertimeRequestData> {
    const formData = objectToFormData(data);
    return this.axios
      .patch<IOvertimeRequestData>(`${this.endpoint}${data.id}/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      .then(({ data }) => data);
  }

  deleteOvertimeRequest(id: number): Promise<any> {
    return this.axios.delete(`${this.endpoint}${id}/`);
  }

  updateOvertimeRequestState(
    data: IUpdateOvertimeStatePayload
  ): Promise<IOvertimeRequestData> {
    return this.axios
      .put<IOvertimeRequestData>(
        `${this.endpoint}${data.id}/set-state/${data.state}/`,
        {
          assignee: data.assignee,
          comment: data.comment
        }
      )
      .then(({ data }) => data);
  }

  // comments
  fetchOvertimeRequestComments(
    overtimeId: number
  ): Promise<IMentionAttachmentCommentData[]> {
    return this.axios
      .get<IMentionAttachmentCommentData[]>(
        `${this.endpoint}${overtimeId}/comments/`
      )
      .then(({ data }) => data);
  }

  fetchOvertimeRequestComment(
    overtimeId: number,
    commentId: number
  ): Promise<IMentionAttachmentCommentData> {
    return this.axios
      .get<IMentionAttachmentCommentData>(
        `${this.endpoint}${overtimeId}/comments/${commentId}/`
      )
      .then(({ data }) => data);
  }

  createOvertimeRequestComment(
    overtimeId: number,
    comment: IMentionAttachmentCommentData
  ): Promise<IMentionAttachmentCommentData> {
    return this.axios
      .post<IMentionAttachmentCommentData>(
        `${this.endpoint}${overtimeId}/comments/`,
        comment
      )
      .then(({ data }) => data);
  }

  updateOvertimeRequestComment(
    overtimeId: number,
    comment: IMentionAttachmentCommentData
  ): Promise<IMentionAttachmentCommentData> {
    return this.axios
      .patch<IMentionAttachmentCommentData>(
        `${this.endpoint}${overtimeId}/comments/${comment.id}/`,
        comment
      )
      .then(({ data }) => data);
  }

  fetchOvertimeRequestActivities(id: number): Promise<Transition[]> {
    return this.axios
      .get<Transition[]>(`${this.endpoint}${id}/activities/`)
      .then(({ data }) => data);
  }

  deleteOvertimeRequestComment(
    overtimeId: number,
    commentId: number
  ): Promise<void> {
    return this.axios
      .delete(`${this.endpoint}${overtimeId}/comments/${commentId}/`)
      .then(({ data }) => data);
  }

  addOvertimeRequestCommentAttachments(
    overtimeId: number,
    commentId: number,
    attachments: IAttachmentData[]
  ): Promise<IMentionAttachmentCommentData> {
    const formData = objectToFormData({ attachments });
    return this.axios
      .post<IMentionAttachmentCommentData>(
        `${this.endpoint}${overtimeId}/comments/${commentId}/add-attachments/`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      )
      .then(({ data }) => data);
  }

  removeOvertimeRequestCommentAttachment(
    overtimeId: number,
    commentId: number,
    attachmentId: number
  ): Promise<void> {
    return this.axios
      .delete(
        `${this.endpoint}${overtimeId}/comments/${commentId}/remove-attachment/${attachmentId}/`
      )
      .then();
  }

  fetchOvertimeRequestCommentsMentionableUsers(
    overtimeId: number
  ): Promise<IUserData[]> {
    return this.axios
      .get(`${this.endpoint}${overtimeId}/comments/mentionable-users/`)
      .then(({ data }) => data);
  }

  downloadOvertimeAttachment(id: number, attachmentId: number) {
    return this.axios
      .get<Blob>(`${this.endpoint}${id}/download-attachment/${attachmentId}/`, {
        responseType: 'blob'
      })
      .then(({ data }) => data);
  }

  deleteOvertimeAttachment(id: number, attachmentId: number) {
    return this.axios
      .delete(`${this.endpoint}${id}/remove-attachment/${attachmentId}/`)
      .then(({ data }) => data);
  }
}
